/**
 * Typed GTM dataLayer events.
 *
 * The client-side GTM container (and the server-side container feeding GA4 /
 * Meta CAPI behind it) triggers on these exact event names and payload shapes.
 * They mirror what the legacy storefront (`apps/web`) pushed, which is what
 * the container's triggers and tags were built against — changing a name or a
 * field is a breaking change for customer analytics and needs a container
 * review first (US-11215 / US-11216 / US-11219).
 */

/** GA4-style ecommerce item, as the legacy storefront shaped it. */
export type EcommerceItem = {
  item_id: string;
  item_name: string;
  affiliation: 'Understory';
  item_category: string;
  item_category2?: string;
  item_category3?: string;
  item_variant?: string;
  price?: number;
  quantity?: number;
};

/**
 * The receipt's richer item shape (adds VAT detail).
 *
 * The category fields are sent under both spellings. `item_category2` /
 * `item_category3` match every other event (and GA4's own naming), and are
 * what new container mappings should read. The underscored `item_category_2` /
 * `item_category_3` are the original 2023 spelling, unique to this event — the
 * server-side container and an unknown number of customer containers map them,
 * so they stay until those are migrated (US-12225).
 */
export type ReceiptEcommerceItem = {
  item_id: string | null;
  item_name: string;
  affiliation: string;
  item_category: string;
  item_category2: string;
  item_category3: string;
  /** @deprecated Duplicate of `item_category2`; read that instead. */
  item_category_2: string;
  /** @deprecated Duplicate of `item_category3`; read that instead. */
  item_category_3: string;
  price: number;
  quantity: number;
  currency: string;
  vat_rate: number;
  vat_amount: number;
};

/** The receipt's ecommerce data. Sent twice on `on_receipt` — spread at the
 *  top level (like every other event) and again under `payload`. */
export type ReceiptEcommerce = {
  receiptId: string;
  receiptCreatedDate: string;
  currency: string;
  value: number;
  items: ReceiptEcommerceItem[];
};

/**
 * The booked session — an Understory event id, distinct from the experience
 * id (one experience runs many sessions). Event-level rather than per item:
 * an order is always for a single session.
 *
 * Named `experience_event_id` rather than `event_id` on purpose — `event` is
 * GTM's key for the event *name*, GA4 has its own `session_id`, and Meta's
 * `eventID` is the conversion dedup key (our `transaction_id`). A bare
 * `event_id` would read as the analytics hit's id (US-12237).
 *
 * Absent, never empty, when there is no session to name: open-hours bookings
 * have no event until the booking is made, so their `add_to_cart` /
 * `begin_checkout` omit it and it first appears on `on_receipt`. Gift cards
 * and punch cards never have one.
 */
type BookedSession = {
  experience_event_id?: string;
};

/**
 * Spreads the session id in, or nothing at all. The key is omitted rather than
 * sent empty: an absent dataLayer key is unambiguous in GTM, where `null` or
 * `''` reads as data and would resolve to a variable's value.
 */
export function bookedSession(eventId: string | null | undefined) {
  return eventId ? { experience_event_id: eventId } : {};
}

/**
 * The guest's email and phone as Google's Enhanced Conversions take
 * pre-hashed user-provided data: each value is the lowercase hex SHA-256 of the
 * normalized email, or of the phone in E.164. Hashed on the server, so the
 * plain values never reach the page, where the customer's own container runs
 * too. A key is absent when its value is missing or couldn't be normalized
 * (US-15179).
 */
export type HashedUserData = {
  sha256_email_address?: string;
  sha256_phone_number?: string;
};

/**
 * Every event the storefront pushes to the dataLayer via `tracker.event()`,
 * keyed by event name. (`page_view` is pushed by `tracker.pageview()` and is
 * deliberately not part of this map.)
 */
export type TrackerEventPayloads = {
  /** Experience detail page viewed. */
  view_item: {
    currency: string;
    value: number;
    items: EcommerceItem[];
  };
  /** An experience picked from a listing (home page grid). */
  select_item: {
    item_list_id: string;
    item_list_name: string;
    items: EcommerceItem[];
  };
  /** Tickets chosen and the visitor continued toward booking info.
   *  `totalPrice` (not `value`) is the legacy field name the container maps. */
  add_to_cart: BookedSession & {
    currency: string;
    totalPrice: number;
    items: EcommerceItem[];
  };
  /** The info step submitted toward payment ("Go to Payment"). */
  begin_checkout: BookedSession & {
    currency: string;
    value: number;
    items: EcommerceItem[];
  };
  /** Receipt page viewed after a completed order. `transaction_id` dedupes
   *  conversions (e.g. Meta Purchase event IDs).
   *
   *  The ecommerce data is sent twice: spread at the top level, which matches
   *  every other event here and GA4's own `purchase` shape, and again under
   *  `payload`, the original 2023 nesting that existing container mappings
   *  read. Both carry the same object (US-12225). */
  on_receipt: ReceiptEcommerce &
    BookedSession & {
      transaction_id: string;
      /** For the Google Ads conversion's user-provided data: a GTM Data
       *  Layer Variable named `user_data`. Only there when the company has
       *  "Include user data" on for Google Ads, and absent, never empty, when
       *  the order has neither an email nor a phone that could be normalized
       *  (US-15179). */
      user_data?: HashedUserData;
      /** @deprecated Duplicate of the top-level fields; read those instead.
       *  Frozen at its 2023 shape — new fields go on the top level only, so
       *  `experience_event_id` and `user_data` are deliberately not in here. */
      payload: ReceiptEcommerce;
    };
};

export type TrackerEventName = keyof TrackerEventPayloads;

/** One basket line as the payload builders need it. `variantId` is the basket
 *  item id and keeps its kind prefix (`variant/<id>` or `addon/<id>`). */
export type TicketTrackingLine = {
  variantId: string;
  variantName: string;
  unitPriceCents: number;
  quantity: number;
};

type ExperienceRef = {
  experienceId: string;
  experienceName: string;
};

/** Legacy naming rule: the default variant (`variant/<experienceId>`) shows
 *  the bare experience name; every other variant/add-on gets a suffix. */
function itemName(
  { experienceId, experienceName }: ExperienceRef,
  line: TicketTrackingLine
): string {
  return line.variantId === `variant/${experienceId}`
    ? experienceName
    : `${experienceName} - ${line.variantName}`;
}

export function buildViewItemPayload(
  input: ExperienceRef & { currency: string; priceCents: number }
): TrackerEventPayloads['view_item'] {
  return {
    currency: input.currency,
    value: input.priceCents / 100,
    items: [
      {
        item_id: input.experienceId,
        item_name: input.experienceName,
        affiliation: 'Understory',
        item_category: 'experience',
      },
    ],
  };
}

export function buildSelectItemPayload(
  input: ExperienceRef & { listId: string; listName: string }
): TrackerEventPayloads['select_item'] {
  return {
    item_list_id: input.listId,
    item_list_name: input.listName,
    items: [
      {
        item_id: input.experienceId,
        item_name: input.experienceName,
        affiliation: 'Understory',
        item_category: 'experience',
      },
    ],
  };
}

export function buildAddToCartPayload(
  input: ExperienceRef & {
    currency: string;
    lines: TicketTrackingLine[];
    /** Omitted for open-hours bookings — see {@link BookedSession}. */
    experienceEventId?: string;
  }
): TrackerEventPayloads['add_to_cart'] {
  const lines = input.lines.filter((l) => l.quantity > 0);
  return {
    ...bookedSession(input.experienceEventId),
    currency: input.currency,
    totalPrice:
      lines.reduce((acc, l) => acc + l.unitPriceCents * l.quantity, 0) / 100,
    items: lines.map((l) => ({
      item_id: input.experienceId,
      item_name: itemName(input, l),
      affiliation: 'Understory',
      item_category: l.variantId.startsWith('addon/') ? 'addon' : 'experience',
      item_variant: l.variantId,
      price: l.unitPriceCents / 100,
      quantity: l.quantity,
    })),
  };
}

export function buildBeginCheckoutPayload(
  input: ExperienceRef & {
    currency: string;
    totalCents: number;
    lines: TicketTrackingLine[];
    /** Omitted for open-hours bookings — see {@link BookedSession}. */
    experienceEventId?: string;
  }
): TrackerEventPayloads['begin_checkout'] {
  return {
    ...bookedSession(input.experienceEventId),
    currency: input.currency,
    value: input.totalCents / 100,
    items: input.lines
      .filter((l) => l.quantity > 0)
      .map((l) => ({
        item_id: l.variantId,
        item_name: l.variantName,
        affiliation: 'Understory',
        item_category: 'variant/experience',
        item_category2: input.experienceName,
        item_category3: input.experienceId,
        price: l.unitPriceCents / 100,
        quantity: l.quantity,
      })),
  };
}

/** Gift card and punch card each push a single-item `begin_checkout`. The
 *  `item_name` / `item_category` constants are fixed by their consumers —
 *  customer GTM containers and our server-side container were built against
 *  these exact values — not by anything in our own data. */
function buildStoredValueBeginCheckoutPayload(
  item: Pick<EcommerceItem, 'item_id' | 'item_name' | 'item_category'>,
  input: { currency: string; priceCents: number; totalCents: number }
): TrackerEventPayloads['begin_checkout'] {
  return {
    currency: input.currency,
    // Fee-inclusive, as the booking flow does it: `value` is what the visitor
    // pays, while `price` stays the item's own amount.
    value: input.totalCents / 100,
    items: [
      {
        ...item,
        affiliation: 'Understory',
        price: input.priceCents / 100,
        quantity: 1,
      },
    ],
  };
}

export function buildGiftCardBeginCheckoutPayload(input: {
  companyId: string;
  currency: string;
  priceCents: number;
  totalCents: number;
}): TrackerEventPayloads['begin_checkout'] {
  return buildStoredValueBeginCheckoutPayload(
    {
      // The company, not the gift card — legacy sent it this way and the
      // containers key on it.
      item_id: input.companyId,
      item_name: 'Voucher',
      item_category: 'voucher',
    },
    input
  );
}

export function buildPunchcardBeginCheckoutPayload(input: {
  tokenTypeId: string;
  currency: string;
  priceCents: number;
  totalCents: number;
}): TrackerEventPayloads['begin_checkout'] {
  return buildStoredValueBeginCheckoutPayload(
    {
      item_id: input.tokenTypeId,
      item_name: 'Punchcard',
      item_category: 'punchcard',
    },
    input
  );
}
